import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, Sparkles, Check, X, RefreshCw } from "lucide-react";

const STATUS_CLS = {
  pending: "bg-gold/15 text-gold-deep",
  approved: "bg-sage/25 text-ink",
  rejected: "bg-ink/10 text-ink-muted line-through",
};

export default function AdminPlainWords() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/plain-words`, auth);
      setCards(data.cards || []);
    } catch (e) { if (e.response?.status === 401) adminLogout(); }
    finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try { await axios.post(`${API}/admin/plain-words/generate`, {}, { ...auth, timeout: 120000 }); await load(); }
    catch (e) { if (e.response?.status === 401) adminLogout(); }
    finally { setGenerating(false); }
  };

  const update = async (id, patch) => {
    try { await axios.put(`${API}/admin/plain-words/${id}`, patch, auth); await load(); }
    catch (e) { if (e.response?.status === 401) adminLogout(); }
  };

  const edit = (id, field, value) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));

  const counts = cards.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-plain-words">
      <div className="mx-auto max-w-4xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
              <Sparkles className="h-6 w-6 text-gold-deep" /> Plain Words
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-soft">
              Optional plain-language cards for the densest pages. Nothing shows to readers until you approve it —
              and the book's original words never change. Review, edit, approve or reject each one.
            </p>
            <p className="mt-2 text-xs text-ink-muted">
              {cards.length} drafted · {counts.approved || 0} approved · {counts.pending || 0} pending · {counts.rejected || 0} rejected
            </p>
          </div>
          <button onClick={generate} disabled={generating} data-testid="generate-plain-words"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 transition hover:bg-ink/90 disabled:opacity-50">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {cards.length ? "Draft any missing" : "Draft the 15 cards"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-4 px-5 py-6 sm:px-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : !cards.length ? (
          <div className="rounded-2xl bg-ivory-50 p-10 text-center ring-1 ring-ivory-400/50" data-testid="plain-words-empty">
            <Sparkles className="mx-auto h-7 w-7 text-ink-muted" />
            <p className="mt-3 font-display text-lg text-ink">No cards yet</p>
            <p className="mt-1 text-sm text-ink-muted">Draft plain-language cards for the 15 densest pages, then review them here.</p>
          </div>
        ) : (
          cards.map((c) => {
            const d = drafts[c.id] || {};
            const plain = d.plain ?? c.plain;
            const example = d.example ?? c.example;
            return (
              <div key={c.id} className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50" data-testid={`plain-word-${c.page}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display text-ink">Page {c.page} <span className="text-ink-muted">· {c.chapter}</span> <span className="text-xs text-ink-muted">· FK {c.fk_grade}</span></p>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs capitalize ${STATUS_CLS[c.status] || ""}`}>{c.status}</span>
                </div>
                {c.source_excerpt && (
                  <p className="mt-2 border-l-2 border-ivory-400 pl-3 text-xs italic leading-relaxed text-ink-muted">{c.source_excerpt.slice(0, 220)}…</p>
                )}
                <label className="mt-3 block text-xs text-ink-muted">Plain version</label>
                <textarea rows={2} value={plain} onChange={(e) => edit(c.id, "plain", e.target.value)} data-testid={`plain-input-${c.page}`}
                  className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                <label className="mt-2 block text-xs text-ink-muted">Everyday example</label>
                <textarea rows={2} value={example} onChange={(e) => edit(c.id, "example", e.target.value)} data-testid={`example-input-${c.page}`}
                  className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => update(c.id, { plain, example })} data-testid={`save-${c.page}`}
                    className="rounded-full border border-ivory-400/70 px-3.5 py-1.5 text-xs font-display text-ink hover:border-gold/50">Save edits</button>
                  <button onClick={() => update(c.id, { plain, example, status: "approved" })} data-testid={`approve-${c.page}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-sage/30 px-3.5 py-1.5 text-xs font-display text-ink hover:bg-sage/40"><Check className="h-3.5 w-3.5" /> Approve &amp; publish</button>
                  <button onClick={() => update(c.id, { status: "rejected" })} data-testid={`reject-${c.page}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-display text-ink-muted hover:text-terracotta-deep"><X className="h-3.5 w-3.5" /> Reject</button>
                  {c.status !== "pending" && (
                    <button onClick={() => update(c.id, { status: "pending" })} className="rounded-full px-3.5 py-1.5 text-xs text-ink-muted hover:text-ink">Back to pending</button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
